import { OutlinedDropdownOption } from '@/shared/components/OutlinedDropdown';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { permissionsApiService } from '../api/permissionsApiService';
import { PermissionsPayload } from '../types/permissionsType';

// Reverse of PERMISSION_KEY_MAP: backendCamelKey → frontend option value
// Built once at module load for O(1) lookup during template hydration.

// ─── Translations ─────────────────────────────────────────────────────────────

const translations: Record<string, string> = {
  'Select All': 'Zaznacz wszystko',

  'Facility Management': 'Zarządzanie placówkami',
  'List of Facilities': 'Lista placówek',
  'Create Facility': 'Tworzenie placówki',
  'Edit Facility': 'Edycja placówki',
  'Activate/Deactivate Facility': 'Aktywacja / dezaktywacja placówki',
  'View List of Facilities': 'Lista placówek',
  'View Specific Facility Details': 'Podgląd szczegółów placówki',

  'Equipment Management': 'Zarządzanie aparaturą',
  'Add Equipment': 'Dodaj aparaturę',
  'Edit Equipment Details': 'Edycja danych aparatury',
  'View Equipment List': 'Lista aparatury',
  'View Equipment Details': 'Podgląd danych aparatury',
  'Activate/Deactivate Equipment': 'Aktywacja / dezaktywacja aparatury',

  'Facility User Management / Employees':
    'Facility User Management / Employees',
  'Add Facility User': 'Add Facility User',
  'Edit Facility User': 'Edit Facility User',
  'View Facility User List': 'View Facility User List',
  'View Facility User Details': 'View Facility User Details',
  'Activate / Deactivate Facility User': 'Activate / Deactivate Facility User',

  'Service Request Management': 'Zarządzanie zgłoszeniami serwisowymi',
  'Create Service Request': 'Tworzenie zgłoszenia serwisowego',
  'View Service Request List': 'Lista zgłoszeń serwisowych',
  'View Specific Service Request': 'Podgląd zgłoszenia serwisowego',
  'Cancel Service Request': 'Anulowanie zgłoszenia serwisowego',

  'QMS Document Management': 'Zarządzanie dokumentami SZJ',
  'List All QMS Documents': 'Lista dokumentów SZJ',
  'View Specific QMS Document': 'Podgląd dokumentu SZJ',
  'Download QMS Documents': 'Pobieranie dokumentów SZJ',
  'Share QMS Documents': 'Udostępnianie dokumentów SZJ',
  'Request Change in QMS Document': 'Zgłoszenie zmiany w dokumencie SZJ',

  'Certificate Management': 'Zarządzanie certyfikatami',
  'List Certificates': 'Lista certyfikatów',
  'Download Certificate': 'Pobieranie certyfikatu',

  'Invoice Management': 'Zarządzanie fakturami',
  'List Invoices': 'Lista faktur',
  'View Specific Invoice': 'Podgląd faktury',
  'Download Invoice': 'Pobieranie faktury',

  Others: 'Pozostałe',
  'View Dashboard': 'Podgląd panelu głównego',
  'View Support Contact Details': 'Podgląd danych kontaktowych wsparcia',
  'Contact Request': 'Tworzenie zgłoszenia kontaktowego',
};

export const tPermission = (key: string) => translations[key] ?? key;

// ─── Initial permissions data (English labels; translated at render time) ─────

export const initialPermissionsData = [
  {
    title: 'Facility Management',
    options: [
      {
        label: 'View List of Facilities',
        value: 'view_list_facilities',
        checked: true,
        disabled: true,
      },
      {
        label: 'Create Facility',
        value: 'add_facility',
        checked: false,
      },
      {
        label: 'Edit Facility',
        value: 'edit_facility',
        checked: false,
      },
      {
        label: 'View Specific Facility Details',
        value: 'view_specific_facility',
        checked: true,
        disabled: true,
      },
    ],
  },
  {
    title: 'Equipment Management',
    options: [
      {
        label: 'View Equipment List',
        value: 'view_equipment_list',
        checked: false,
      },
      {
        label: 'Add Equipment',
        value: 'add_equipment',
        checked: false,
      },
      {
        label: 'Edit Equipment Details',
        value: 'edit_equipment_details',
        checked: false,
      },
      {
        label: 'View Equipment Details',
        value: 'view_equipment_details',
        checked: false,
      },
    ],
  },
  {
    title: 'Facility User Management / Employees',
    options: [
      {
        label: 'View Facility User List',
        value: 'view_facility_user_list',
        checked: false,
      },
      {
        label: 'Add Facility User',
        value: 'add_facility_user',
        checked: false,
      },
      {
        label: 'Edit Facility User',
        value: 'edit_facility_user',
        checked: false,
      },
      {
        label: 'View Facility User Details',
        value: 'view_facility_user_details',
        checked: false,
      },
      {
        label: 'Activate / Deactivate Facility User',
        value: 'activate_deactivate_facility_user',
        checked: false,
      },
    ],
  },
  {
    title: 'Service Request Management',
    options: [
      {
        label: 'View Service Request List',
        value: 'view_service_request_list',
        checked: false,
      },
      {
        label: 'Create Service Request',
        value: 'create_service_request',
        checked: false,
      },
      {
        label: 'View Specific Service Request',
        value: 'view_specific_service_request',
        checked: false,
      },
      {
        label: 'Cancel Service Request',
        value: 'cancel_service_request',
        checked: false,
      },
    ],
  },
  {
    title: 'QMS Document Management',
    options: [
      {
        label: 'List All QMS Documents',
        value: 'list_all_qms',
        checked: false,
      },
      {
        label: 'View Specific QMS Document',
        value: 'view_specific_qms',
        checked: false,
      },
      {
        label: 'Download QMS Documents',
        value: 'download_qms',
        checked: false,
      },
      {
        label: 'Share QMS Documents',
        value: 'share_qms',
        checked: false,
      },
      {
        label: 'Request Change in QMS Document',
        value: 'request_change_qms',
        checked: false,
      },
    ],
  },
  {
    title: 'Certificate Management',
    options: [
      {
        label: 'List Certificates',
        value: 'list_certificates',
        checked: false,
      },
      {
        label: 'Download Certificate',
        value: 'download_certificate',
        checked: false,
      },
    ],
  },
  {
    title: 'Invoice Management',
    options: [
      {
        label: 'List Invoices',
        value: 'list_invoices',
        checked: false,
      },
      {
        label: 'View Specific Invoice',
        value: 'view_specific_invoice',
        checked: false,
      },
      {
        label: 'Download Invoice',
        value: 'download_invoice',
        checked: false,
      },
    ],
  },
  {
    title: 'Others',
    options: [
      {
        label: 'View Dashboard',
        value: 'view_dashboard',
        checked: false,
      },
      {
        label: 'View Support Contact Details',
        value: 'view_support_contact',
        checked: false,
      },
      {
        label: 'Contact Request',
        value: 'contact_request',
        checked: false,
      },
    ],
  },
];

// ─── Frontend option value → backend camelCase permission key ─────────────────
// equipmentActivateDeactivate has no UI element — omitted intentionally.
export const PERMISSION_KEY_MAP: Readonly<Record<string, string>> = {
  // Facility Management
  view_list_facilities: 'facilityList',
  add_facility: 'facilityCreate',
  edit_facility: 'facilityEdit',
  view_specific_facility: 'facilityView',
  // Equipment Management
  view_equipment_list: 'equipmentList',
  add_equipment: 'equipmentCreate',
  edit_equipment_details: 'equipmentEdit',
  view_equipment_details: 'equipmentView',
  // Facility User Management
  view_facility_user_list: 'facilityUserList',
  add_facility_user: 'facilityUserCreate',
  edit_facility_user: 'facilityUserEdit',
  view_facility_user_details: 'facilityUserView',
  activate_deactivate_facility_user: 'facilityUserActivateDeactivate',
  // Service Request Management
  view_service_request_list: 'serviceRequestList',
  create_service_request: 'serviceRequestCreate',
  view_specific_service_request: 'serviceRequestView',
  cancel_service_request: 'serviceRequestCancel',
  // QMS Document Management
  list_all_qms: 'qmsDocumentList',
  view_specific_qms: 'qmsDocumentView',
  download_qms: 'qmsDocumentDownload',
  share_qms: 'qmsDocumentShare',
  request_change_qms: 'qmsDocumentRequestChange',
  // Certificate Management
  list_certificates: 'certificateList',
  download_certificate: 'certificateDownload',
  // Invoice Management
  list_invoices: 'invoiceList',
  view_specific_invoice: 'invoiceView',
  download_invoice: 'invoiceDownload',
  // Others
  view_dashboard: 'dashboardView',
  view_support_contact: 'supportContactView',
  contact_request: 'contactRequestCreate',
};

// ─── Permission rules lookup table ────────────────────────────────────────────
// Derived from "Pozytron Role Privileges - Permission.xlsx"
//   onCheck   — permissions to auto-check when this permission is checked
//   onUncheck — permissions to auto-uncheck when this permission is unchecked
//               (= permissions present in CHECKED row but absent in UNCHECKED row)
export const PERMISSION_RULES: Record<
  string,
  { onCheck: string[]; onUncheck: string[] }
> = {
  // ── Service Request Management ───────────────────────────────────────────────
  create_service_request: {
    onCheck: ['view_service_request_list', 'view_specific_service_request'],
    onUncheck: [],
  },
  view_service_request_list: {
    onCheck: [],
    onUncheck: [
      'create_service_request',
      'view_specific_service_request',
      'cancel_service_request',
    ],
  },
  view_specific_service_request: {
    onCheck: ['view_service_request_list'],
    onUncheck: [],
  },
  cancel_service_request: {
    onCheck: [
      'create_service_request',
      'view_service_request_list',
      'view_specific_service_request',
    ],
    onUncheck: [],
  },

  // ── QMS Document Management ──────────────────────────────────────────────────
  list_all_qms: {
    onCheck: [],
    onUncheck: [
      'view_specific_qms',
      'download_qms',
      'share_qms',
      'request_change_qms',
    ],
  },
  view_specific_qms: {
    onCheck: ['list_all_qms'],
    onUncheck: [],
  },
  download_qms: {
    onCheck: ['list_all_qms', 'view_specific_qms'],
    onUncheck: [],
  },
  share_qms: {
    onCheck: ['list_all_qms', 'view_specific_qms', 'download_qms'],
    onUncheck: [],
  },
  request_change_qms: {
    onCheck: ['list_all_qms', 'view_specific_qms'],
    onUncheck: [],
  },

  // ── Certificate Management ───────────────────────────────────────────────────
  // Checking list_certificates auto-checks view_equipment_list (required for the
  // equipment filter dropdown). Unchecking either cascades the other off.
  list_certificates: {
    onCheck: ['view_equipment_list', 'view_equipment_details'],
    onUncheck: ['download_certificate', 'view_equipment_list'],
  },
  download_certificate: {
    onCheck: ['list_certificates'],
    onUncheck: [],
  },

  // ── Invoice Management ───────────────────────────────────────────────────────
  list_invoices: {
    onCheck: [],
    onUncheck: ['view_specific_invoice', 'download_invoice'],
  },
  view_specific_invoice: {
    onCheck: ['list_invoices'],
    onUncheck: [],
  },
  download_invoice: {
    onCheck: ['list_invoices', 'view_specific_invoice'],
    onUncheck: [],
  },

  // ── Facility Management ──────────────────────────────────────────────────────
  add_facility: {
    onCheck: [
      'view_list_facilities',
      'view_specific_facility',
      'add_facility_user',
      'view_facility_user_list',
      'view_facility_user_details',
      'add_equipment',
      'view_equipment_list',
      'view_equipment_details',
    ],
    onUncheck: [
      'add_facility_user',
      'view_facility_user_list',
      'view_facility_user_details',
      'add_equipment',
      'view_equipment_list',
      'view_equipment_details',
    ],
  },
  edit_facility: {
    onCheck: [
      'view_list_facilities',
      'view_specific_facility',
      'view_facility_user_list',
      'edit_facility_user',
      'view_facility_user_details',
      'view_equipment_list',
      'edit_equipment_details',
      'view_equipment_details',
    ],
    onUncheck: [
      'view_facility_user_list',
      'edit_facility_user',
      'view_facility_user_details',
      'view_equipment_list',
      'edit_equipment_details',
      'view_equipment_details',
    ],
  },
  view_list_facilities: {
    onCheck: [],
    onUncheck: [
      'add_facility',
      'edit_facility',
      'view_specific_facility',
      'add_facility_user',
      'edit_facility_user',
      'view_facility_user_list',
      'view_facility_user_details',
      'activate_deactivate_facility_user',
      'add_equipment',
      'view_equipment_list',
      'view_equipment_details',
      'edit_equipment_details',
    ],
  },
  view_specific_facility: {
    onCheck: ['view_list_facilities'],
    onUncheck: [],
  },

  // ── Equipment Management ─────────────────────────────────────────────────────
  add_equipment: {
    onCheck: [
      'add_facility',
      'view_list_facilities',
      'view_specific_facility',
      'view_equipment_list',
      'view_equipment_details',
    ],
    onUncheck: [],
  },
  view_equipment_list: {
    onCheck: ['view_equipment_details'],
    onUncheck: [
      'add_equipment',
      'edit_equipment_details',
      'view_equipment_details',
      'list_certificates',
    ],
  },
  view_equipment_details: {
    onCheck: ['view_list_facilities', 'view_specific_facility'],
    onUncheck: [],
  },
  edit_equipment_details: {
    onCheck: [
      'view_list_facilities',
      'edit_facility',
      'view_specific_facility',
      'view_equipment_list',
      'view_equipment_details',
    ],
    onUncheck: [],
  },

  // ── Facility User Management ─────────────────────────────────────────────────
  add_facility_user: {
    onCheck: [
      'add_facility',
      'view_list_facilities',
      'view_specific_facility',
      'view_facility_user_list',
      'view_facility_user_details',
    ],
    onUncheck: [],
  },
  edit_facility_user: {
    onCheck: [
      'view_list_facilities',
      'edit_facility',
      'view_specific_facility',
      'view_facility_user_list',
      'view_facility_user_details',
    ],
    onUncheck: [],
  },
  view_facility_user_list: {
    onCheck: [],
    onUncheck: [
      'add_facility_user',
      'edit_facility_user',
      'view_facility_user_details',
      'activate_deactivate_facility_user',
    ],
  },
  view_facility_user_details: {
    onCheck: [
      'view_list_facilities',
      'view_specific_facility',
      'view_equipment_details',
    ],
    onUncheck: ['view_equipment_details'],
  },
  activate_deactivate_facility_user: {
    onCheck: [
      'add_facility',
      'view_list_facilities',
      'edit_facility',
      'view_facility_user_list',
    ],
    onUncheck: [],
  },

  // ── Others ───────────────────────────────────────────────────────────────────
  view_dashboard: {
    onCheck: ['view_support_contact'],
    onUncheck: ['view_support_contact'],
  },
  view_support_contact: {
    onCheck: [],
    onUncheck: [],
  },
  contact_request: {
    onCheck: [],
    onUncheck: [],
  },
};

// ─── Dependency propagation logic ─────────────────────────────────────────────
// Uses PERMISSION_RULES directly: onCheck auto-checks, onUncheck auto-unchecks.

const applyDependencyLogic = (
  currentData: typeof initialPermissionsData,
  updatedModuleIndex: number,
  updatedOptions: OutlinedDropdownOption[]
) => {
  const newData: typeof initialPermissionsData = JSON.parse(
    JSON.stringify(currentData)
  );
  const targetModule = newData[updatedModuleIndex];
  if (targetModule) {
    targetModule.options = updatedOptions.map((o) => ({
      ...o,
      checked: o.checked ?? false,
    }));
  }

  const prevOptions = currentData[updatedModuleIndex]?.options ?? [];
  const checkedByUser = new Set<string>();
  const uncheckedByUser = new Set<string>();
  prevOptions.forEach((prev) => {
    const curr = updatedOptions.find((o) => o.value === prev.value);
    if (curr) {
      if (!prev.checked && curr.checked) {
        checkedByUser.add(prev.value);
      }
      if (prev.checked && !curr.checked) {
        uncheckedByUser.add(prev.value);
      }
    }
  });

  const valueToOption: Record<string, OutlinedDropdownOption> = {};
  newData.forEach((module) => {
    module.options.forEach((opt) => {
      valueToOption[opt.value] = opt;
    });
  });

  // Apply user unchecks to valueToOption first so stillProvided sees the correct state
  uncheckedByUser.forEach((val) => {
    const opt = valueToOption[val];
    if (opt) {
      opt.checked = false;
    }
  });

  // Apply check cascades from PERMISSION_RULES
  checkedByUser.forEach((val) => {
    PERMISSION_RULES[val]?.onCheck.forEach((depVal) => {
      const opt = valueToOption[depVal];
      if (
        opt &&
        !opt.disabled &&
        !opt.checked &&
        !uncheckedByUser.has(depVal)
      ) {
        opt.checked = true;
      }
    });
  });

  // Direct cascade targets (one level from user unchecks) are always force-unchecked,
  // bypassing stillProvided. This ensures e.g. unchecking "View Equipment List"
  // always unchecks Add/Edit/View Equipment regardless of other providers.
  const forceUncheck = new Set<string>(uncheckedByUser);
  uncheckedByUser.forEach((val) => {
    PERMISSION_RULES[val]?.onUncheck.forEach((depVal) => {
      forceUncheck.add(depVal);
      const opt = valueToOption[depVal];
      if (opt && !opt.disabled) {
        opt.checked = false;
      }
    });
  });

  // Collect deeper cascade unchecks (targets of force-unchecked items).
  const toUncheck = new Set<string>(forceUncheck);
  forceUncheck.forEach((val) => {
    PERMISSION_RULES[val]?.onUncheck.forEach((depVal) => {
      toUncheck.add(depVal);
    });
  });

  // Apply deeper cascade unchecks with stillProvided guard.
  toUncheck.forEach((depVal) => {
    if (forceUncheck.has(depVal) || checkedByUser.has(depVal)) {
      return;
    }
    const opt = valueToOption[depVal];
    if (!opt || opt.disabled || !opt.checked) {
      return;
    }
    const stillProvided = Object.keys(valueToOption).some((otherVal) => {
      if (toUncheck.has(otherVal)) {
        return false;
      }
      const otherOpt = valueToOption[otherVal];
      if (!otherOpt?.checked) {
        return false;
      }
      return PERMISSION_RULES[otherVal]?.onCheck.includes(depVal) ?? false;
    });
    if (!stillProvided) {
      opt.checked = false;
    }
  });

  // Defensive pass: disabled options must always remain checked
  newData.forEach((module) => {
    module.options.forEach((opt) => {
      if ((opt as OutlinedDropdownOption).disabled) {
        opt.checked = true;
      }
    });
  });

  return newData;
};
// ─── Environment flag ─────────────────────────────────────────────────────────

const apiDomain = (import.meta.env['VITE_API_DOMAIN_NAME'] as string) ?? '';
export const showLangToggle =
  import.meta.env.DEV || apiDomain.includes('dev') || apiDomain.includes('qa');

// ─── Hook ─────────────────────────────────────────────────────────────────────

const VALIDATION_ERROR_MESSAGE =
  'Wybierz co najmniej jedno uprawnienie, aby dodać klienta.';

const SUCCESS_MESSAGE = 'Permissions granted to all clients successfully.';

function buildPayload(
  perms: typeof initialPermissionsData
): PermissionsPayload {
  const payload: PermissionsPayload = {};
  perms.forEach((module) => {
    module.options.forEach((opt) => {
      const backendKey = PERMISSION_KEY_MAP[opt.value];
      if (backendKey) {
        payload[backendKey] = opt.checked ?? false;
      }
    });
  });
  return payload;
}

export const usePermission = () => {
  const navigate = useNavigate();

  const [permissions, setPermissions] = useState(initialPermissionsData);
  const [isPolish, setIsPolish] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [validationError, setValidationError] = useState('');

  // Snapshot of the loaded template — used to detect "no edits" on cancel.
  const originalCheckedRef = useRef<Record<string, boolean> | null>(null);

  const buildCheckedSnapshot = (
    modules: typeof initialPermissionsData
  ): Record<string, boolean> => {
    const snapshot: Record<string, boolean> = {};
    for (const module of modules) {
      for (const opt of module.options) {
        snapshot[opt.value] = Boolean(opt.checked);
      }
    }
    return snapshot;
  };

  useEffect(() => {
    const loadTemplate = async () => {
      const result = await permissionsApiService.getTemplate();
      if (result.success && result.template) {
        // Build flat map: backendCamelKey → allowed
        const allowedMap: Record<string, boolean> = {};
        for (const privilege of result.template.privileges) {
          for (const perm of privilege.permissions) {
            allowedMap[perm.key] = perm.allowed;
          }
        }
        setPermissions((prev) => {
          const next = prev.map((module) => ({
            ...module,
            options: module.options.map((opt) => {
              if (opt.disabled) {
                return opt;
              }
              const backendKey = PERMISSION_KEY_MAP[opt.value];
              if (!backendKey) {
                return opt;
              }
              return { ...opt, checked: allowedMap[backendKey] ?? false };
            }),
          }));
          originalCheckedRef.current = buildCheckedSnapshot(next);
          return next;
        });
      } else {
        // No saved template — snapshot the defaults so any toggle counts as a change.
        originalCheckedRef.current = buildCheckedSnapshot(permissions);
      }
      setIsLoading(false);
    };
    void loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const translate = useCallback(
    (key: string) => (isPolish ? tPermission(key) : key),
    [isPolish]
  );

  const allOptions = permissions.flatMap((m) => m.options);
  const isAllSelected = allOptions.every((o) => o.checked);
  const isIndeterminate = !isAllSelected && allOptions.some((o) => o.checked);

  const handleDropdownChange = (
    index: number,
    updatedOptions: OutlinedDropdownOption[]
  ) => {
    if (validationError) {
      setValidationError('');
    }
    const currentModule = permissions[index];
    const normalized = updatedOptions.map((opt) => ({
      ...(currentModule?.options.find((o) => o.value === opt.value) ?? opt),
      checked: opt.checked ?? false,
    }));
    setPermissions(applyDependencyLogic(permissions, index, normalized));
  };

  const handleSelectAll = () => {
    if (validationError) {
      setValidationError('');
    }
    const next = !isAllSelected;
    setPermissions(
      permissions.map((module) => ({
        ...module,
        options: module.options.map((o) =>
          o.disabled ? o : { ...o, checked: next }
        ),
      }))
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const hasSelection = permissions
      .flatMap((m) => m.options)
      .some((o) => o.checked);

    if (!hasSelection) {
      setValidationError(VALIDATION_ERROR_MESSAGE);
      return;
    }

    setValidationError('');
    setIsSubmitting(true);

    const result = await permissionsApiService.saveTemplate(
      buildPayload(permissions)
    );

    if (!result.success) {
      setSnackbarError(result.error ?? 'Nie udało się zapisać uprawnień.');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      return;
    }

    navigate('/Klienci', { state: { successMessage: SUCCESS_MESSAGE } });
  };

  const hasChanges = (() => {
    const original = originalCheckedRef.current;
    if (!original) {
      return false;
    }
    for (const module of permissions) {
      for (const opt of module.options) {
        if (Boolean(opt.checked) !== Boolean(original[opt.value])) {
          return true;
        }
      }
    }
    return false;
  })();

  const handleCancel = () => {
    if (!hasChanges) {
      navigate('/Klienci');
      return;
    }
    setIsCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setIsCancelModalOpen(false);
  };

  const handleConfirmCancel = () => {
    setIsCancelModalOpen(false);
    navigate('/Klienci');
  };

  const handleCloseSnackbar = () => {
    setIsSnackbarOpen(false);
  };

  return {
    permissions,
    isPolish,
    setIsPolish,
    isLoading,
    isSubmitting,
    isCancelModalOpen,
    isSnackbarOpen,
    snackbarError,
    validationError,
    isAllSelected,
    isIndeterminate,
    translate,
    handleDropdownChange,
    handleSelectAll,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
  };
};
